import {
  useAnalystCardsQuery,
  usePinAnalystCardMutation,
  useUnpinAnalystCardMutation,
} from '@transactions/graphql';
import {cardSourceSchema, chartSpecSchema} from 'common/analyst';
import * as React from 'react';
import {AnalystCardBody} from 'src/AnalystView/AnalystCardBody';
import {useAnalystChat} from 'src/AnalystView/useAnalystChat';
import {Button} from 'src/Button';
import {cn} from 'src/lib/utils';
import type {CardSpec} from 'common/analyst';
import type {AnalystTurn} from 'src/AnalystView/useAnalystChat';

/**
 * The Analyst tab: the pinned board (cards that re-run their sources
 * against live data on every visit) above the chat where the agent
 * explores the ledger and proposes new cards.
 */

const SUGGESTIONS = [
  'How is my spending trending this year vs last?',
  'What are my five biggest payees this year?',
  'Which categories grew the most over the last 90 days?',
];

const PinnedCard = ({
  id,
  question,
  title,
  commentary,
  source,
  chart,
  onUnpin,
}: {
  id: string;
  question: string;
  title: string;
  commentary: string | null;
  source: unknown;
  chart: unknown;
  onUnpin: (id: string) => void;
}) => {
  const [showSource, setShowSource] = React.useState(false);
  const parsedSource = cardSourceSchema.safeParse(source);
  const parsedChart = chartSpecSchema.safeParse(chart);
  return (
    <div className="border-border rounded-lg border p-4">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <h3 className="text-foreground text-sm font-semibold">{title}</h3>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => setShowSource((v) => !v)}
            className="text-muted-foreground hover:text-foreground text-xs"
          >
            Source
          </button>
          <button
            type="button"
            onClick={() => onUnpin(id)}
            className="text-muted-foreground hover:text-foreground text-xs"
          >
            Unpin
          </button>
        </div>
      </div>
      <div className="text-muted-foreground mb-2 text-xs">{question}</div>
      {parsedSource.success && parsedChart.success ? (
        <AnalystCardBody
          source={parsedSource.data}
          chart={parsedChart.data}
        />
      ) : (
        <div className="text-destructive py-4 text-center text-sm">
          This card&apos;s stored spec is no longer valid — unpin and re-ask.
        </div>
      )}
      {commentary != null && commentary.length > 0 && (
        <p className="text-muted-foreground mt-2 text-xs">{commentary}</p>
      )}
      {showSource && parsedSource.success && (
        <pre className="bg-muted text-muted-foreground mt-2 max-h-48 overflow-auto rounded p-2 text-[11px]">
          {JSON.stringify(parsedSource.data, null, 2)}
        </pre>
      )}
    </div>
  );
};

const ProposedCard = ({
  card,
  question,
  onPin,
}: {
  card: CardSpec;
  question: string;
  onPin: (question: string, card: CardSpec) => Promise<boolean>;
}) => {
  // Pinned-ness is this proposal's own mutation outcome — titles are
  // model-generated display text with no uniqueness, so they can never be
  // the identity. 'pinning' disables the button the instant the first
  // click lands, so a double-click cannot mint duplicate cards.
  const [state, setState] = React.useState<'idle' | 'pinning' | 'pinned'>(
    'idle',
  );
  return (
    <div className="border-border bg-card rounded-lg border p-4">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <h3 className="text-foreground text-sm font-semibold">{card.title}</h3>
        <Button
          variant="outline"
          className="px-2 py-0.5 text-xs"
          disabled={state !== 'idle'}
          onClick={() => {
            setState('pinning');
            void onPin(question, card).then((ok) => {
              setState(ok ? 'pinned' : 'idle');
            });
          }}
        >
          {state === 'pinned' ? 'Pinned' : state === 'pinning' ? 'Pinning…' : 'Pin'}
        </Button>
      </div>
      <AnalystCardBody source={card.source} chart={card.chart} />
      {card.commentary != null && card.commentary.length > 0 && (
        <p className="text-muted-foreground mt-2 text-xs">{card.commentary}</p>
      )}
    </div>
  );
};

const Turn = ({
  turn,
  onPin,
}: {
  turn: AnalystTurn;
  onPin: (question: string, card: CardSpec) => Promise<boolean>;
}) => (
  <div className="flex flex-col gap-2">
    <div className="self-end">
      <div className="bg-primary text-primary-foreground max-w-xl rounded-lg px-3 py-1.5 text-sm">
        {turn.question}
      </div>
    </div>
    {turn.tools.length > 0 && (
      <div className="flex flex-wrap gap-1.5">
        {turn.tools.map((tool) => (
          <span
            key={tool.toolCallId}
            className={cn(
              'rounded-full border px-2 py-0.5 text-[11px]',
              tool.done
                ? tool.ok
                  ? 'border-border text-muted-foreground'
                  : 'border-destructive text-destructive'
                : 'border-border text-foreground animate-pulse',
            )}
            title={tool.summary}
          >
            {tool.name}
          </span>
        ))}
      </div>
    )}
    {turn.cards.map((card, i) => (
      <ProposedCard key={i} card={card} question={turn.question} onPin={onPin} />
    ))}
    {turn.text.length > 0 && (
      <div className="text-foreground max-w-3xl text-sm whitespace-pre-wrap">
        {turn.text}
      </div>
    )}
    {turn.error != null && (
      <div className="text-destructive text-sm">{turn.error}</div>
    )}
    {turn.streaming && turn.text.length === 0 && turn.tools.length === 0 && (
      <div className="text-muted-foreground text-sm">Thinking…</div>
    )}
  </div>
);

export const AnalystView = () => {
  const {data, error, refetch} = useAnalystCardsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [pinCard] = usePinAnalystCardMutation();
  const [unpinCard] = useUnpinAnalystCardMutation();
  const {turns, busy, ask} = useAnalystChat();
  const [draft, setDraft] = React.useState('');
  const bottomRef = React.useRef<HTMLDivElement>(null);

  const cards = data?.analystCards ?? [];

  const handlePin = async (
    question: string,
    card: CardSpec,
  ): Promise<boolean> => {
    try {
      await pinCard({variables: {question, card}});
      void refetch();
      return true;
    } catch {
      return false;
    }
  };
  const handleUnpin = (id: string) => {
    void unpinCard({
      variables: {id},
      onCompleted: () => void refetch(),
    });
  };

  const submit = () => {
    const question = draft.trim();
    if (question.length === 0 || busy) {
      return;
    }
    setDraft('');
    void ask(question).then(() => {
      bottomRef.current?.scrollIntoView({behavior: 'smooth'});
    });
  };

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4 p-4">
      <div>
        <h1 className="text-foreground text-xl font-semibold">Insights</h1>
        <p className="text-muted-foreground text-sm">
          Ask questions about the ledger; pin the answers. Pinned cards
          re-run their query against live data on every visit.
        </p>
      </div>

      {error != null && (
        <div className="text-destructive text-sm">
          Couldn&apos;t load pinned cards: {error.message}
        </div>
      )}
      {cards.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {cards.map((card) => (
            <PinnedCard
              key={card._id}
              id={card._id}
              question={card.question}
              title={card.title}
              commentary={card.commentary ?? null}
              source={card.source}
              chart={card.chart}
              onUnpin={handleUnpin}
            />
          ))}
        </div>
      )}

      <div className="flex flex-col gap-4">
        {turns.map((turn, i) => (
          <Turn key={i} turn={turn} onPin={handlePin} />
        ))}
        <div ref={bottomRef} />
      </div>

      {turns.length === 0 && (
        <div className="flex flex-wrap gap-2">
          {SUGGESTIONS.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => void ask(suggestion)}
              className="border-border text-muted-foreground hover:text-foreground rounded-full border px-3 py-1 text-xs"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
          placeholder={busy ? 'Working…' : 'Ask about your money…'}
          disabled={busy}
          className="border-border bg-background text-foreground placeholder:text-muted-foreground flex-1 rounded-lg border px-3 py-2 text-sm"
        />
        <Button variant="primary" onClick={submit} disabled={busy}>
          Ask
        </Button>
      </div>
    </div>
  );
};
